import React, { useCallback, useMemo } from "react";
import styles from "./activity-column.module.css";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { Trans } from "@coworker/locales";
import { LoadMore } from "../../../shared/components/LoadMore/LoadMore";
import { useItemsInfoQueries } from "../../../shared/hooks/item/useItemsInfo";
import { Item } from "../../../shared/types/item";
import { FixaSkeleton } from "../../../shared/wrappers/FixaSkeleton/FixaSkeleton";
import { FullTask } from "../../types/FullTask";
import { useTeamId, useUID } from "@coworker/app/src/core/auth/useLoggedInUser";
import StackIconPath from "../../../shared/wrappers/FixaSSRIcon/paths/StackIconPath";
import { ModalContext } from "../../../layout/context/ModalContextProvider";
import { ModalType } from "../../../layout/context/modalType";
import { MobileColumnSkeleton } from "./MobileColumnSkeleton";
import trackerHelper from "@coworker/app/src/helpers/tracker";
import { TaskType } from "@coworker/apprestructured/src/tasks/enums/taskTypes";
import { TaskListItem } from "@coworker/apprestructured/src/activity/hooks/useActivityData";
import { SwipeableTaskCard } from "@coworker/apprestructured/src/tasks/components/TaskCard/SwipeableTaskCard";
import { shouldDisplayPickupTask } from "@coworker/app/src/components/TaskDetail/actions/helpers";
import TaskCard from "@coworker/apprestructured/src/task-management/components/TaskCard/TaskCard";
import ListableTaskCardWrapper from "@coworker/apprestructured/src/task-management/components/TaskCard/wrappers/NormalTaskWrapper/ListableTaskCardWrapper";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import GroupTitle from "@coworker/apprestructured/src/activity/components/column/GroupTitle/GroupTitle";
import { getRefillAreaIdFromLocation } from "@fixa/platform-shared/dist/storeLocations/categoriseLocations";

type TaskOrHeaderListItem =
  | TaskListItem
  | { type: "group-title"; areaId: string };

function getGroupByLocation(task: FullTask, groupBy: string) {
  switch (groupBy) {
    case "hfbPickup": {
      return task.pickup_location || task.pickup_location_custom || "";
    }
    case "hfbRefill": {
      return task.location_custom || task.location || "";
    }
    default: {
      return "";
    }
  }
}

interface ActivityColumnProps {
  title: string;
  tasks: TaskListItem[];
  filteredCount: number;
  totalCount: number;
  loadNextPage: () => void;
  tab: string;
  activeTab: string;
  appliedFiltersCount: number;
  isLoading: boolean;
  isFetching: boolean;
  resetFilters: () => void;
  groupBy: string;
  onShowTaskDetails: (taskId: string, taskType: TaskType) => void;
}

export const ActivityColumn = ({
  title,
  tasks,
  filteredCount,
  totalCount,
  loadNextPage,
  tab,
  activeTab,
  appliedFiltersCount,
  isLoading,
  isFetching,
  resetFilters,
  groupBy,
  onShowTaskDetails,
}: ActivityColumnProps) => {
  const itemNos = tasks
    .filter(({ type }) => type === "v1")
    .map(({ task_data: task }) => (task as FullTask).product_article_id)
    .filter(Boolean);

  const itemsInfoResult = useItemsInfoQueries(itemNos);

  const itemsInfo = itemsInfoResult
    .map((result) => result.data)
    .filter(Boolean);

  const uid = useUID();
  const { toggleModal } = React.useContext(ModalContext);

  const teamId = useTeamId();

  const [, setSearchParams] = useSearchParamState();

  const handleTaskV2CardClick = useCallback(
    (taskId: string) => {
      setSearchParams((params) => {
        params.set("taskViewer", String(taskId));
      });
    },
    [setSearchParams]
  );

  const tasksAndHeaders: TaskOrHeaderListItem[] = useMemo(() => {
    if (!groupBy) {
      return tasks;
    }

    const result: TaskOrHeaderListItem[] = [];
    let prevAreaId: string | null = null;
    tasks.forEach((taskListItem) => {
      const { type, task_data: task } = taskListItem;
      if (type === "v1") {
        const location = getGroupByLocation(task, groupBy);
        const areaId = getRefillAreaIdFromLocation(location, {
          enableSFM: true,
        });
        if (areaId !== prevAreaId) {
          result.push({ type: "group-title", areaId });
          prevAreaId = areaId;
        }
      }
      result.push(taskListItem);
    });
    return result;
  }, [groupBy, tasks]);

  return (
    <div
      className={`${styles["column"]} ${activeTab === tab && styles["active"]}`}
    >
      <div className={styles["title-wrapper"]}>
        {(tab === "OPEN" || tab === "IN_PROGRESS") && (
          <FixaButton
            iconOnly
            ssrIcon={StackIconPath}
            size="xsmall"
            type="tertiary"
            className={styles["multi-select-button"] as string}
            onClick={() => {
              trackerHelper.trackMultiSelectButtonTab(tab);
              toggleModal({
                type: ModalType.MultiSelectTasks,
                payload: { type: tab === "OPEN" ? "open" : "my" },
              });
            }}
          />
        )}
        <div className={styles["title"]}>
          <Trans>{title}</Trans>{" "}
          {!isLoading && !isFetching && filteredCount < totalCount && (
            <span className={styles["count"]}>
              ({filteredCount}/{totalCount}
              {totalCount >= 1000 && "+"})
            </span>
          )}
          {!isLoading && !isFetching && filteredCount === totalCount && (
            <span className={styles["count"]}>
              {totalCount}
              {totalCount >= 1000 && "+"}
            </span>
          )}
        </div>
      </div>
      <div className={styles["wrapper"]}>
        {!isLoading &&
          tasksAndHeaders.length > 0 &&
          tasksAndHeaders.map((taskOrHeaderListItem) => {
            const { type } = taskOrHeaderListItem;
            switch (type) {
              case "v1": {
                const { task_data: task } = taskOrHeaderListItem;
                return (
                  <SwipeableTaskCard
                    key={`v1_${task.id}`}
                    task={task}
                    item={
                      itemsInfo?.find(
                        (item) => item?.no === task.product_article_id
                      ) as Item
                    }
                    multiSelect={false}
                    isMyTasks={task.assigned_user_id === uid}
                    type={tab}
                    disableSwipe={
                      task.state === "COMPLETED" || task.state === "CLOSED"
                    }
                    disablePickup={
                      !shouldDisplayPickupTask(task, {
                        uid: "",
                        team_id: teamId,
                        role_id: "",
                      })
                    }
                    onShowTaskDetails={onShowTaskDetails}
                  />
                );
              }
              case "v2": {
                const { task_data: task } = taskOrHeaderListItem;
                return (
                  <ListableTaskCardWrapper
                    key={`v2_${task.id}`}
                    onSelect={() => handleTaskV2CardClick(task.id)}
                    aria-label={task.title}
                  >
                    <TaskCard task={task} />
                  </ListableTaskCardWrapper>
                );
              }
              case "group-title": {
                const { areaId } = taskOrHeaderListItem;
                return <GroupTitle key={`area_${areaId}`} areaId={areaId} />;
              }
            }
          })}
        {!isLoading && !isFetching && !tasks.length && (
          <div className={styles["no-results"]}>
            <div className={styles["no-results-title"]}>
              <Trans>noTaskResultsString</Trans>
            </div>
            {!tasks.length && appliedFiltersCount > 0 ? (
              <>
                <div className={styles["no-results-subtitle"]}>
                  <Trans>noTasksWithAppliedFiltersString</Trans>
                </div>
                <FixaButton
                  onClick={resetFilters}
                  text={<Trans>clearFiltersString</Trans>}
                  size="small"
                  type="secondary"
                />
              </>
            ) : (
              <div className={styles["no-results-subtitle"]}>
                <Trans>noTasksFoundString</Trans>
              </div>
            )}
          </div>
        )}
        {(isLoading || isFetching) && (
          <>
            <div className={styles["mobile-skeleton"]}>
              <MobileColumnSkeleton activeTab={activeTab} tab={tab} />
            </div>
            <div className={styles["desktop-skeleton"]}>
              <FixaSkeleton height="100%" />
            </div>
          </>
        )}
        {tasks.length < filteredCount && !isLoading && !isFetching && (
          <LoadMore
            onLoadMoreClick={loadNextPage}
            loadedItemsCount={tasks.length}
            totalCount={filteredCount}
            isFetching={isFetching}
          />
        )}
      </div>
    </div>
  );
};
